import React, { FC, useEffect, useState } from "react";
import * as Feather from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AuthUserType } from "../../redux/actions/authUserActions";
import { fetchUser } from "../../redux/actions/userActions";
import { StoreState } from "../../redux/reducers";
import { AppDispatch } from "../../redux/store";

export interface MenuItemTypes {
  key: string;
  label: string;
  isTitle?: boolean;
  Icon?: Feather.Icon;
  url?: string;
  badge?: {
    variant: string;
    text: string;
  };
  parentKey?: string;
  target?: string;
  children?: MenuItemTypes[];
}

export const MENU_ITEMS: MenuItemTypes[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    isTitle: false,
    Icon: Feather.Monitor,
    url: "/",
  },
  {
    key: "branch",
    label: "Branch",
    isTitle: false,
    Icon: Feather.BarChart,
    url: "/branch",
  },
  {
    key: "course",
    label: "Course",
    isTitle: false,
    Icon: Feather.BookOpen,
    url: "/course",
  },
  {
    key: "feesmaster",
    label: "Fees Master",
    isTitle: false,
    Icon: Feather.DollarSign,
    url: "/fees-master",
  },
  {
    key: "student",
    label: "Student",
    isTitle: false,
    Icon: Feather.Users,
    url: "/student",
  },
  {
    key: "payment",
    label: "Payment",
    isTitle: false,
    Icon: Feather.ChevronDown,
    children: [
      {
        key: "studentpayment",
        label: "Billing",
        url: "/student-billing",
        Icon: Feather.Repeat,
        parentKey: "payment",
      },
      {
        key: "studentconcession",
        label: "Concession",
        url: "/student-concession",
        Icon: Feather.Repeat,
        parentKey: "payment",
      },
      {
        key: "studentmiscfees",
        label: "Miscellaneous",
        url: "/student-misc-fees",
        Icon: Feather.Repeat,
        parentKey: "payment",
      },
      {
        key: "studentcancelfees",
        label: "Cancel Billing",
        url: "/student-cancel-billing",
        Icon: Feather.Repeat,
        parentKey: "payment",
      },
      {
        key: "studentrefundfees",
        label: "Refund Billing",
        url: "/student-refund-billing",
        Icon: Feather.Repeat,
        parentKey: "payment",
      },
      {
        key: "transaction",
        label: "Transaction",
        url: "/transaction",
        Icon: Feather.Server,
        parentKey: "payment",
      },
    ],
  },
  {
    key: "accountHolder",
    label: "Account Holder",
    isTitle: false,
    Icon: Feather.Users,
    url: "/account-holder",
  },
  {
    key: "user",
    label: "User",
    isTitle: false,
    Icon: Feather.User,
    url: "/user",
  },
  {
    key: "dropdownMaster",
    label: "DropDown Master",
    isTitle: false,
    Icon: Feather.List,
    url: "/dropdown-master",
  },
  {
    key: "rolemanagement",
    label: "Role Management",
    isTitle: false,
    Icon: Feather.CheckSquare,
    url: "/role-permissions",
  },
  {
    key: "reports",
    label: "Reports",
    isTitle: false,
    Icon: Feather.ChevronDown,
    children: [
      {
        key: "daybookreport",
        label: "Day Book",
        url: "/daybook_report",
        Icon: Feather.Repeat,
        parentKey: "reports",
      },
      {
        key: "feesdetailsreport",
        label: "Student Fees Title Details",
        url: "/student-fees-report",
        Icon: Feather.Repeat,
        parentKey: "reports",
      },
      {
        key: "studentsummaryreport",
        label: "Student Fees Summary",
        url: "/fees-summary-report",
        Icon: Feather.Repeat,
        parentKey: "reports",
      },
      {
        key: "totalfeesreport",
        label: "Total Fees Summary",
        url: "/totalfees-summary-report",
        Icon: Feather.Repeat,
        parentKey: "reports",
      },
      {
        key: "billingreport",
        label: "Billing Report",
        url: "/bill-report",
        Icon: Feather.Repeat,
        parentKey: "reports",
      },
      {
        key: "yearwisestudentfeesdetails",
        label: "Year WiseStudent Fees Details",
        url: "/student-fees-details",
        Icon: Feather.Repeat,
        parentKey: "reports",
      },
      {
        key: "totalstudentfeessummary",
        label: "Total Wise Student Fees Summary",
        url: "/total-student-fees-summary",
        Icon: Feather.Repeat,
        parentKey: "reports",
      },
      {
        key: "Incomeandexpenditurereport",
        label: "Income And Expenditure ",
        url: "/inome-expenditure",
        Icon: Feather.Repeat,
        parentKey: "reports",
      },
    ],
  },
  {
    key: "studentFormPDF",
    label: "Student Form PDF",
    isTitle: false,
    Icon: Feather.CheckSquare,
    url: "/student-form-pdf",
  },
];

const SideNavBar: FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const authUser = useSelector<StoreState, AuthUserType>(
    (state: any) => state.authUser,
  );
  const authenticated =
    typeof authUser.token === "string" &&
    authUser.token !== undefined &&
    authUser.token !== "";

  useEffect(() => {
    if (authenticated) {
      dispatch(fetchUser());
    }
  }, [authenticated]);

  const rolesWithView =
    authUser.rolePermissions
      ?.filter((rp) => rp.pageUrl && String(rp.view) === "true")
      .map((mrp) => mrp.pageUrl)
      .filter((url): url is string => url !== null && url !== undefined) || [];

  const shouldDisplayMenuItem = (menuItem: MenuItemTypes): boolean => {
    if (authUser.role === "SUPER") return true;

    if (menuItem.key === "dashboard") return true;

    if (menuItem.url && rolesWithView.includes(menuItem.url)) {
      return true;
    }

    if (menuItem.children) {
      return menuItem.children.some(
        (child) => child.url && rolesWithView.includes(child.url),
      );
    }

    return false;
  };


  const renderMenuItem = (menuItem: MenuItemTypes) => {
    const { Icon } = menuItem;
    return (
      <li className="nav-item" key={menuItem.key}>
        <Link to={menuItem.url || ""} className="nav-link">
          {Icon && <Icon style={{ marginRight: "5px" }} />}
          <p>{menuItem.label}</p>
        </Link>
      </li>
    );
  };

  const filteredMenuItems = MENU_ITEMS.filter((menuItem) =>
    shouldDisplayMenuItem(menuItem),
  );
  return (
    <aside
      className="main-sidebar elevation-4"
      style={{ backgroundColor: "#10296C", position: "fixed", height: "100vh" }}
    >
      <div
        className="sidebar"
        style={{
          height: "100%",
          overflowY: "auto",
        }}
      >
        <div className="user-panel mt-3 pb-3 mb-3 d-flex">
          <div className="image">
            <img
              src="https://i.pinimg.com/550x/90/c8/d0/90c8d0fde36b92c18a6c19a06b4b2735.jpg"
              className="img-circle elevation-2"
              alt="User"
            />
          </div>
          <div className="info">
            <Link to="#" className="d-block">
              College Management
            </Link>
          </div>
        </div>
        <nav className="mt-2">
          <ul
            className="nav nav-pills nav-sidebar flex-column"
            data-widget="treeview"
            data-accordion="false"
          >
            {filteredMenuItems.map((menuItem) =>
              menuItem.children ? (
                <MenuItem
                  key={menuItem.key}
                  menuItem={menuItem}
                  rolesWithView={rolesWithView}
                  isSuperUser={authUser.role === "SUPER"}
                />
              ) : (
                renderMenuItem(menuItem)
              ),
            )}
            <li className="nav-item">
              <Link to="/logout" className="nav-link">
                <Feather.LogOut style={{ marginRight: "5px" }} />
                <p>Logout</p>
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </aside>
  );
};

const MenuItem: FC<{
  menuItem: MenuItemTypes;
  rolesWithView: string[];
  isSuperUser: boolean;
}> = ({ menuItem, rolesWithView, isSuperUser }) => {
  const [showSubMenu, setShowSubMenu] = useState(false);
  const { Icon } = menuItem;

  const filteredChildren = isSuperUser
    ? menuItem.children
    : menuItem.children?.filter(
        (child) => child.url && rolesWithView.includes(child.url),
      );

  if (!filteredChildren || filteredChildren.length === 0) {
    return null;
  }

  const handleToggleMenuClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowSubMenu(!showSubMenu);
  };

  return (
    <li className="nav-item" key={menuItem.key}>
      <Link
        to="#"
        className="nav-link"
        onClick={handleToggleMenuClick}
        aria-expanded={showSubMenu}
      >
        {Icon && <Icon style={{ marginRight: "5px" }} />}
        <p>
          {menuItem.label}
          <i
            className={`right fas ${!showSubMenu ? "fa-angle-left" : "fa-angle-down"}`}
          ></i>
        </p>
      </Link>
      <ul
        className="nav nav-treeview"
        style={{ display: showSubMenu ? "block" : "none" }}
      >
        {filteredChildren.map((childItem) => (
          <li className="nav-item" key={childItem.key}>
            <Link to={childItem.url || "#"} className="nav-link">
              <i className="fas fa-angle-right nav-icon"></i>
              <p>{childItem.label}</p>
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
};

export default SideNavBar;
